import type React from "react";
import {useCallback} from "react";

import {
  faArrowDown,
  faArrowUp,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";

import {useLevelV2} from "~/api/hooks/useLevelV2";
import CustomButton from "~/component/common/CustomButton";
import useLevelLoader from "~/contexts/loader/useLevelLoader";

interface PlaylistItemProps {
  levelId: string;
  levelIds: string[];
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDelete: () => void;
}

const PlaylistItem: React.FC<PlaylistItemProps> = ({
  levelId,
  levelIds,
  onMoveUp,
  onMoveDown,
  onDelete,
}) => {
  const {data: levelData, isLoading} = useLevelV2({id: levelId});
  const levelLoader = useLevelLoader();

  const handleHover = useCallback(() => {
    if (levelData) {
      levelLoader(levelId, levelData);
    }
  }, [levelData, levelId, levelLoader]);

  return (
    <div
      className="flex items-center justify-between p-2 mb-2 rounded-lg bg-gradient-to-b from-black/50 to-indigo-900/50"
      onMouseEnter={handleHover}
      onTouchStart={handleHover}
    >
      <div className="flex-1">
        {isLoading ? (
          <span>Loading...</span>
        ) : (
          <h2 className="text-lg font-normal truncate">
            {levelData?.name || levelId}
          </h2>
        )}
      </div>
      <div className="flex gap-1">
        <CustomButton
          className="bg-gradient-to-br from-blue-600 via-blue-500 to-black text-white disabled:text-gray-300 px-2 py-1 rounded-full disabled:from-gray-500 disabled:via-gray-400 disabled:to-gray-300"
          onClick={onMoveUp}
          disabled={levelIds.indexOf(levelId) === 0}
          title="Move Up"
        >
          <FontAwesomeIcon icon={faArrowUp} className="text-xl" />
        </CustomButton>

        <CustomButton
          className="bg-gradient-to-br from-blue-600 via-blue-500 to-black text-white disabled:text-gray-300 px-2 py-1 rounded-full disabled:from-gray-500 disabled:via-gray-400 disabled:to-gray-300"
          onClick={onMoveDown}
          disabled={levelIds.indexOf(levelId) === levelIds.length - 1}
          title="Move Down"
        >
          <FontAwesomeIcon icon={faArrowDown} className="text-xl" />
        </CustomButton>

        <CustomButton
          className="bg-gradient-to-br from-red-600 via-red-500 to-black text-white px-2 py-1 rounded-full transition-colors hover:from-red-700 hover:via-red-600 hover:to-black"
          onClick={onDelete}
          title="Remove Level"
        >
          <FontAwesomeIcon icon={faTrash} className="text-xl" />
        </CustomButton>
      </div>
    </div>
  );
};

export default PlaylistItem;
